import { useState, useEffect } from 'react';
import Seo from '../components/Seo'
import { useFamilyStore } from '../stores/familyStore';
import { useApiService } from '../services/api';
import Layout from '../components/layout/Layout';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../stores/authStore';
import { useEntitlements } from '../hooks/useEntitlements';
import { track, checkoutParams } from '../utils/analytics';

export default function Pricing() {
	const [yearly, setYearly] = useState(false);
	const [actionLoading, setActionLoading] = useState<boolean>(false);
	const { family, initialized, initialize, isLoading } = useFamilyStore();
	const api = useApiService();
  const user = useAuthStore(state => state.user);

	// Uprawnienia – tylko rodzic A może zarządzać subskrypcją
	const { isParentA } = usePermissions();
	const { planId: activePlan, priceId } = useEntitlements(family?.id);
	const hasStripeSubscription = !!priceId;

	useEffect(() => {
		// Jeśli użytkownik jest zalogowany, a store nie jest zainicjalizowany – zainicjalizuj
		if (!initialized && !isLoading) {
			initialize().catch(() => {});
		}
	}, [initialized, isLoading, initialize]);

	async function startCheckout(lookupKey: string) {
		if (!family) {
			alert('Zaloguj się i wybierz rodzinę, aby kontynuować.');
			return;
		}
		try {
			setActionLoading(true);
			track('begin_checkout', checkoutParams(lookupKey));
			// {CHECKOUT_SESSION_ID} podstawia Stripe — służy jako transaction_id zdarzenia purchase
			const { url } = await api.createCheckoutSession({
				familyId: family.id,
				lookupKey,
				successUrl: `${window.location.origin}/calendar?purchase=success&plan=${lookupKey}&session_id={CHECKOUT_SESSION_ID}`,
				cancelUrl: `${window.location.origin}/pricing?canceled=1`
			});
			window.location.href = url;
		} catch (e: any) {
			alert(`Nie udało się rozpocząć zakupu: ${e?.message || 'spróbuj ponownie później.'}`);
		} finally {
			setActionLoading(false);
		}
	}

	async function openPortal() {
		if (!family) {
			alert('Zaloguj się i wybierz rodzinę, aby kontynuować.');
			return;
		}
		try {
			setActionLoading(true);
			const { url } = await api.createBillingPortalSession({ familyId: family.id, returnUrl: `${window.location.origin}/pricing` });
			window.location.href = url;
		} catch (e: any) {
			alert(`Nie udało się otworzyć portalu subskrypcji: ${e?.message || 'sprawdź, czy masz aktywną subskrypcję.'}`);
		} finally {
			setActionLoading(false);
		}
	}

	const monthlyPrices = { basic: '0 zł', pro: '12 zł', max: '19 zł' } as const;
	const yearlyPrices = { basic: '0 zł', pro: '119 zł', max: '189 zł' } as const;
	const priceSuffix = yearly ? ' / rok' : ' / mies.';

	return (
		<Layout>
			<Seo title="Cennik — 2domy.pl" canonical="https://2domy.pl/pricing" robots="index,follow" />
			<div className="max-w-6xl mx-auto px-4 py-10">
				<h1 className="text-2xl sm:text-3xl font-semibold mb-4 text-slate-900">
					Wybierz plan, który pasuje do Twojej rodziny
				</h1>

				{/* Fancy billing toggle */}
				<div className="flex flex-col items-center mb-8">
					<div className="inline-flex items-center bg-white border rounded-full shadow-sm px-1 py-1">
						<button
							onClick={() => setYearly(false)}
							className={`px-5 py-2 text-sm sm:text-base rounded-full transition ${!yearly ? 'bg-blue-600 text-white shadow' : 'text-slate-700 hover:bg-slate-50'}`}
						>
							Miesięcznie
						</button>
						<button
							onClick={() => setYearly(true)}
							className={`px-5 py-2 text-sm sm:text-base rounded-full transition ${yearly ? 'bg-blue-600 text-white shadow' : 'text-slate-700 hover:bg-slate-50'}`}
						>
							Rocznie
						</button>
					</div>
					<div className="mt-4 text-base sm:text-lg font-semibold text-emerald-700">
						{yearly
							? 'Przy rozliczeniu rocznym zyskujesz 2 miesiące za darmo.'
							: 'Wybierz rozliczenie roczne i zyskaj 2 miesiące za darmo.'}
					</div>
				</div>

				{family && (
					<p className="mb-5 text-sm text-slate-600 text-center">Twój plan: <span className="font-semibold">{activePlan ?? '—'}</span></p>
				)}

				{/* Plans grid */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-5">
					{/* Basic */}
					<div className="group relative rounded-2xl p-[2px] bg-gradient-to-r from-slate-200 to-slate-300">
						<div className="rounded-2xl bg-white p-6 h-full transition-transform duration-200 group-hover:-translate-y-1 group-hover:shadow-xl flex flex-col">
							<h2 className="text-xl sm:text-2xl font-bold mb-2 flex items-center gap-2"><span>🌱</span> Basic</h2>
							<div className="text-3xl font-bold mb-2">{(yearly ? yearlyPrices.basic : monthlyPrices.basic)}<span className="text-base font-medium text-slate-500">{priceSuffix}</span></div>
							<ul className="text-sm space-y-1 mb-4 text-slate-700">
								<li>kalendarz dla 1 dziecka</li>
								<li>2 rodziców</li>
								<li>mini historia (10 ostatnich wpisów)</li>
							</ul>
							{!user ? (
								<a href="/auth/register" className="px-4 py-2 rounded w-full bg-blue-600 text-white hover:bg-blue-700 mt-auto text-center">Zarejestruj, aby zacząć</a>
							) : activePlan === 'basic' ? (
								<button className="px-4 py-2 border rounded w-full mt-auto" disabled>Aktywny</button>
							) : (
								<button className="px-4 py-2 border rounded w-full opacity-70 cursor-not-allowed mt-auto" disabled>Plan Basic</button>
							)}
						</div>
					</div>

					{/* Pro */}
					<div className="group relative rounded-2xl p-[2px] bg-gradient-to-r from-blue-400 via-purple-400 to-indigo-400">
						<div className="rounded-2xl bg-white p-6 h-full transition-transform duration-200 group-hover:-translate-y-1 group-hover:shadow-2xl flex flex-col">
							<div className="absolute -top-3 right-4 text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white shadow">Najpopularniejszy</div>
							<h2 className="text-xl sm:text-2xl font-bold mb-2 flex items-center gap-2"><span>🔹</span> Pro</h2>
							<div className="text-3xl font-bold mb-2">{(yearly ? yearlyPrices.pro : monthlyPrices.pro)}<span className="text-base font-medium text-slate-500">{priceSuffix}</span></div>
							<ul className="text-sm space-y-1 mb-5 text-slate-700">
								<li>kalendarz dla max 2 dzieci</li>
								<li>2 rodziców + 1 obserwator</li>
								<li>wydarzenia, notatki, koszty</li>
								<li>raporty (kosztów, opieki)</li>
								<li>historia 30 dni</li>
								<li>bez reklam</li>
							</ul>
							{!user ? (
								<a
									href="/auth/register"
									className="px-4 py-2 rounded w-full bg-blue-600 text-white hover:bg-blue-700 mt-auto text-center"
								>
									Zarejestruj, aby wybrać Pro
								</a>
							) : activePlan === 'pro' ? (
								<button className="px-4 py-2 rounded w-full bg-green-600 text-white mt-auto">Aktywny</button>
							) : (
								<button
									className="px-4 py-2 rounded w-full bg-blue-600 text-white hover:bg-blue-700 mt-auto disabled:opacity-60 disabled:cursor-not-allowed"
									disabled={!family || !isParentA}
									title={!isParentA ? 'Tylko administrator może zmieniać plan' : ''}
									onClick={() => {
										if (hasStripeSubscription) {
											openPortal();
										} else {
											startCheckout(yearly ? 'pro_yearly' : 'pro_monthly');
										}
									}}
								>
									{hasStripeSubscription ? 'Zmień na Pro (Portal)' : 'Wybierz Pro'}
								</button>
							)}
						</div>
					</div>

					{/* Max */}
					<div className="group relative rounded-2xl p-[2px] bg-gradient-to-r from-amber-400 via-pink-400 to-rose-400">
						<div className="rounded-2xl bg-white p-6 h-full transition-transform duration-200 group-hover:-translate-y-1 group-hover:shadow-2xl flex flex-col">
							<h2 className="text-xl sm:text-2xl font-bold mb-2 flex items-center gap-2"><span>⭐</span> Max</h2>
							<div className="text-3xl font-bold mb-2">{(yearly ? yearlyPrices.max : monthlyPrices.max)}<span className="text-base font-medium text-slate-500">{priceSuffix}</span></div>
							<ul className="text-sm space-y-1 mb-5 text-slate-700">
								<li>kalendarze dla max 5 dzieci</li>
								<li>2 rodziców + 3 obserwatorów</li>
								<li>wydarzenia, notatki, koszty</li>
								<li>raporty (kosztów, opieki)</li>
								<li>historia 180 dni</li>
								<li>bez reklam</li>
							</ul>
							{!user ? (
								<a
									href="/auth/register"
									className="px-4 py-2 rounded w-full bg-blue-600 text-white hover:bg-blue-700 mt-auto text-center"
								>
									Zarejestruj, aby wybrać Max
								</a>
							) : activePlan === 'max' ? (
								<button className="px-4 py-2 rounded w-full bg-green-600 text-white mt-auto">Aktywny</button>
							) : (
								<button
									className="px-4 py-2 rounded w-full bg-blue-600 text-white hover:bg-blue-700 mt-auto disabled:opacity-60 disabled:cursor-not-allowed"
									disabled={!family || !isParentA}
									title={!isParentA ? 'Tylko administrator może zmieniać plan' : ''}
									onClick={() => {
										if (hasStripeSubscription) {
											openPortal();
										} else {
											startCheckout(yearly ? 'max_year' : 'max_month');
										}
									}}
								>
									{hasStripeSubscription ? 'Zmień na Max (Portal)' : 'Wybierz Max'}
								</button>
							)}
						</div>
					</div>
				</div>

				{family && isParentA && (
					<div className="mt-8 flex flex-col sm:flex-row gap-3">
						{hasStripeSubscription && (
							<button
								className="px-4 py-2 border rounded disabled:opacity-60"
								onClick={openPortal}
								disabled={actionLoading}
							>
								Zarządzaj subskrypcją (Portal)
							</button>
						)}
						{activePlan && activePlan !== 'basic' && hasStripeSubscription && (
							<button
								className="px-4 py-2 border rounded text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-60"
								onClick={openPortal}
								disabled={actionLoading}
								title="Anuluj subskrypcję, aby wrócić do Basic"
							>
								Anuluj subskrypcję (wróć do Basic)
							</button>
						)}
					</div>
				)}
				{family && !isParentA && (
					<p className="mt-6 text-center text-xs text-slate-500">Tylko administrator może zarządzać subskrypcją</p>
				)}
				{/* Sekcja informacyjna usunięta na prośbę – szczegóły pozostają w Regulaminie i Polityce prywatności */}
			</div>
		</Layout>
	);
}


